import { envConfig } from '@src/utils/envConfig';
import { checkSessionCookieExpiration } from '@talos/kyoko';
import { useEffect, useState } from 'react';
import { requestTokenFromParent, waitForIFrameToken } from '../utils/iframeToken';

export interface UseIFrameTokenResult {
  token: string | undefined;
  error: string | undefined;
  hasValidSession: boolean;
  shouldWaitForToken: boolean;
}

/**
 * Hook to wait for access token when running in an IFrame
 * Only waits for token if user doesn't have a valid session cookie
 */
export function useIFrameToken(
  isInIFrame: () => boolean,
  originalUrl?: string | null,
  timeoutMs: number = 30000
): UseIFrameTokenResult {
  const [token, setToken] = useState<string>();
  const [error, setError] = useState<string>();
  const [hasValidSession, setHasValidSession] = useState(false);

  // Check session validity - run on mount and when originalUrl changes (e.g., after reload)
  useEffect(() => {
    const sessionCookieName = envConfig.VITE_SESSION_COOKIE;
    if (sessionCookieName) {
      const { isExpired } = checkSessionCookieExpiration(sessionCookieName);
      const isValid = !isExpired;
      setHasValidSession(isValid);

      // If we have a valid session, clear any token to prevent re-processing
      if (isValid) {
        setToken(undefined);
        setError(undefined);
      }
    }
  }, [originalUrl]);

  const shouldWaitForToken = isInIFrame() && !hasValidSession;

  useEffect(() => {
    // Don't process tokens if we have a valid session
    if (hasValidSession) {
      return;
    }

    // Only wait for token via PostMessage if we're in an IFrame AND don't have a valid session
    if (!shouldWaitForToken) {
      return;
    }

    // Wait for token via PostMessage
    setError(undefined);

    waitForIFrameToken(timeoutMs)
      .then(receivedToken => {
        setToken(receivedToken);
        setError(undefined);
      })
      .catch(err => {
        setError(err.message || 'Failed to receive access token');
        setToken(undefined);
      });

    // Request token from parent if not received within 1 second
    const requestTimeout = setTimeout(() => {
      requestTokenFromParent();
    }, 1000);

    return () => {
      clearTimeout(requestTimeout);
    };
  }, [shouldWaitForToken, timeoutMs, hasValidSession]);

  return {
    token,
    error,
    hasValidSession,
    shouldWaitForToken,
  };
}
